/*
 * Finder — dynamic finder block
 * ------------------------------------------------------------------
 * <finder-block> rendered by snippets/section-block-finder.liquid
 * (section blocks and the standalone "Finder" section).
 *
 * Cascading dropdowns (up to 3 levels) built from a collection's filters:
 *   Level 0 is fetched from  {url}?view=search.finder
 *   Level n is fetched with the selections of levels 0..n-1 appended, so
 *   each dropdown only lists values that exist within the previous choices.
 * The Search button links to {url}?param0=…&param1=…
 *
 * Data comes from sections/finder-collection.liquid, which renders
 * #finder-data as plain data attributes.
 * Each <select class="finder-dropdown"> carries data-param and data-placeholder.
 */
if (!customElements.get('finder-block')) {
  class FinderBlock extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      const selects = [...this.querySelectorAll('select.finder-dropdown')];
      if (!selects.length) return;

      this.dataset.initialized = 'true';
      this.url = this.dataset.url || '';
      this.searchButton = this.querySelector('.search-button');
      this.levels = selects.map((select, index) => ({
        select,
        param: select.dataset.param,
        placeholder: select.dataset.placeholder || 'Select',
        // Level 0 is always required; others follow their "Required" setting
        required: index === 0 || select.dataset.required !== 'false',
        noResults: false,
      }));

      this.levels.forEach((level, index) => {
        level.select.addEventListener('change', () => this.onChange(index));
      });

      this.loadLevel(0);
    }

    selections(upTo) {
      const out = {};
      this.levels.slice(0, upTo + 1).forEach((level) => {
        if (level.select.value) out[level.param] = level.select.value;
      });
      return out;
    }

    onChange(index) {
      const value = this.levels[index].select.value;

      this.levels.slice(index + 1).forEach((level) => {
        this.fillItems(level.select, level.placeholder, []);
        level.select.disabled = true;
        level.noResults = false;
      });

      if (value && index + 1 < this.levels.length) this.loadLevel(index + 1);
      this.syncButton();
    }

    async fetchData(params = {}) {
      const url = new URL(this.url, window.location.origin);
      url.search = '';
      url.searchParams.set('view', 'search.finder');
      Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));

      const response = await fetch(url.toString(), { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`finder: ${url} responded ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const node = doc.querySelector('#finder-data');
      if (!node) {
        throw new Error(
          `finder: ${url} did not contain #finder-data. ` +
            'Check templates/collection.search.finder.json uses sections/finder-collection.liquid.'
        );
      }

      const filters = {};
      node.querySelectorAll('[data-filter-param]').forEach((group) => {
        filters[group.dataset.filterParam] = [...group.querySelectorAll('[data-value]')].map((v) => ({
          value: v.dataset.value,
          label: v.dataset.label || v.dataset.value,
          count: parseInt(v.dataset.count || '0', 10),
        }));
      });
      return { collection: node.dataset.collection, filters };
    }

    async loadLevel(index) {
      const level = this.levels[index];
      if (!level || !level.param) return;

      this.setLoading(level.select, true);
      try {
        const data = await this.fetchData(this.selections(index - 1));
        const values = data.filters[level.param] || [];
        if (!values.length) {
          console.warn(
            `finder: no values for "${level.param}" on ${this.url}. ` +
              `Filters available: ${Object.keys(data.filters).join(', ') || 'none'}`
          );
        }
        values.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
        this.fillItems(level.select, level.placeholder, values);
        level.select.disabled = values.length === 0;
        level.noResults = values.length === 0;
      } catch (err) {
        console.error(err);
        this.fillItems(level.select, 'Unavailable', []);
        level.select.title = err.message;
        level.select.disabled = true;
        level.noResults = true;
      } finally {
        this.setLoading(level.select, false);
        this.syncButton();
      }
    }

    /* The button is enabled when every required level has a value — except that a
       required level with no results for the choices above it is skipped, so the
       shopper can still search with what they've selected. */
    canSearch() {
      for (const level of this.levels) {
        if (level.select.value) continue;
        if (level.required && !level.noResults) return false;
        break;
      }
      return !!this.levels[0].select.value;
    }

    buildUrl() {
      const url = new URL(this.url, window.location.origin);
      url.search = '';
      Object.entries(this.selections(this.levels.length - 1)).forEach(([k, v]) => url.searchParams.set(k, v));
      return url.pathname + url.search;
    }

    syncButton() {
      if (!this.searchButton) return;
      if (this.canSearch()) {
        this.searchButton.href = this.buildUrl();
        this.searchButton.removeAttribute('disabled');
        this.searchButton.removeAttribute('aria-disabled');
      } else {
        this.searchButton.removeAttribute('href');
        this.searchButton.setAttribute('disabled', 'true');
        this.searchButton.setAttribute('aria-disabled', 'true');
      }
    }

    fillItems(select, placeholder, items) {
      select.innerHTML = '';
      const add = (value, text, disabled = false) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = text;
        if (disabled) option.disabled = true;
        select.appendChild(option);
      };
      add('', placeholder);

      const { popular, other } = this.orderItems(items, select.dataset.popular);
      if (popular.length && other.length) add('', '-- Popular --', true);
      popular.forEach((item) => add(item.value, item.label || item.value));
      if (popular.length && other.length) add('', '-- Other --', true);
      other.forEach((item) => add(item.value, item.label || item.value));
    }

    /* Split items into the configured "popular" ones (kept in the configured
       order, only if they exist) and the rest (alphabetical). */
    orderItems(items, popularCsv) {
      const wanted = (popularCsv || '')
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
      if (!wanted.length) return { popular: [], other: items };

      const matches = (item, name) =>
        item.value.toLowerCase() === name || (item.label || '').toLowerCase() === name;

      const popular = [];
      wanted.forEach((name) => {
        const hit = items.find((item) => matches(item, name) && !popular.includes(item));
        if (hit) popular.push(hit);
      });
      const other = items.filter((item) => !popular.includes(item));
      return { popular, other };
    }

    setLoading(select, isLoading) {
      select.toggleAttribute('data-loading', isLoading);
      select.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }
  }

  customElements.define('finder-block', FinderBlock);
}
